import {
  AccountingReportKind,
  ReportFormat,
} from '~/app/accounting/useGenerateReport'

export type ReportCatalogEntry = {
  kind: AccountingReportKind
  label: string
  /** One line, shown beside the name while choosing. Kept short enough to fit
   * on one line so the picker's height doesn't move between reports. */
  description: string
  formats: ReportFormat[]
  /** What this report is normally wanted as. Everything but the statement is
   * data an accountant loads, so it defaults to CSV. */
  defaultFormat: ReportFormat
  /** Only offered to orgs with tax reports turned on. */
  requiresTaxReports: boolean
  /** Scoped by an as-of instant rather than a period. */
  isAsOf: boolean
}

// The named things an org can generate, in the order they're offered. Anything
// not on this list isn't a report — it's a table export.
export const REPORT_CATALOG: ReportCatalogEntry[] = [
  {
    kind: 'statement',
    label: 'Statement',
    description: 'Balances, transactions and gains in one document',
    formats: ['pdf'],
    defaultFormat: 'pdf',
    requiresTaxReports: false,
    isAsOf: false,
  },
  {
    kind: 'transactions',
    label: 'Transaction history',
    description: 'Every transfer, with full transaction detail',
    formats: ['csv', 'pdf'],
    defaultFormat: 'csv',
    requiresTaxReports: false,
    isAsOf: false,
  },
  {
    kind: 'tokens',
    label: 'Token balances',
    description: 'Opening and closing balances for each token',
    formats: ['csv', 'pdf'],
    defaultFormat: 'csv',
    requiresTaxReports: false,
    isAsOf: false,
  },
  {
    kind: 'realizedGains',
    label: 'Realized gains and losses',
    description: 'Disposals with proceeds, cost basis and term',
    formats: ['csv', 'pdf'],
    defaultFormat: 'csv',
    requiresTaxReports: true,
    isAsOf: false,
  },
  {
    kind: 'lots',
    label: 'Open lots',
    description: 'Lots still open at the end of the period',
    formats: ['csv', 'pdf'],
    defaultFormat: 'csv',
    requiresTaxReports: true,
    isAsOf: true,
  },
  {
    kind: 'lotTimeline',
    label: 'Lots',
    description: 'Every lot opened or closed in the period',
    formats: ['csv', 'pdf'],
    defaultFormat: 'csv',
    requiresTaxReports: true,
    isAsOf: false,
  },
]

export const reportEntry = (kind: AccountingReportKind) =>
  REPORT_CATALOG.find((entry) => entry.kind === kind)

// Report rows outlive the flag that produced them, so a label is resolved for
// every kind rather than only the ones currently on offer.
export const reportKindLabel = (kind: string) =>
  REPORT_CATALOG.find((entry) => entry.kind === kind)?.label ?? kind

export const availableReports = (hasTaxReports: boolean) =>
  REPORT_CATALOG.filter((entry) => hasTaxReports || !entry.requiresTaxReports)

// Every kind takes account scoping and a period; the open-lots report reads
// the period end as its as-of instant and ignores the start.
export const buildReportParams = ({
  entry,
  startDate,
  endDate,
  accountIds,
}: {
  entry: ReportCatalogEntry
  startDate?: Date
  endDate?: Date
  accountIds: string[]
}): Record<string, string> => ({
  ...(accountIds.length > 0 ? { accountIds: accountIds.join(',') } : {}),
  ...(entry.isAsOf
    ? endDate
      ? { openAsOf: endDate.toISOString() }
      : {}
    : {
        ...(startDate ? { startDate: startDate.toISOString() } : {}),
        ...(endDate ? { endDate: endDate.toISOString() } : {}),
      }),
})
